/**
 * A saved report's BAR — its period and filters — against what is saved.
 *
 * The same rule a dashboard follows (docs/reporting/requirements.md FR-4.8):
 * the bar is the viewer's, even the owner's. Changing the period or a filter
 * re-asks the report for whoever changed it, is carried in the URL, and is
 * never an edit. What everyone opens on changes only by an explicit action in
 * edit mode — or by creating a report, a copy or a duplicate, which nobody
 * else has yet.
 *
 * So a spec is two things here: its STRUCTURE (rows, columns, measures, sort,
 * formatting), which Save writes in edit mode, and its bar, which it does not.
 */

import type { ReportDraft } from '@clockwise/report-kit';
import type { FilterNode, ReportSpec } from '@clockwise/semantic-core';
import {
  DATE_RANGE_PRESETS,
  type DateRangeValue,
  resolveDateRange,
} from '@clockwise/shared/date-range';
import { readFilters, writeFilters } from '@/lib/filter-url';
import { samePeriod } from '@/lib/saved-period';
import type { SavedView } from '@/lib/saved-view';

type SpecPreset = (typeof DATE_RANGE_PRESETS)[number];

/** The bar's half of a saved report's URL. Absent fields mean "as saved". */
export interface BarSearch {
  f?: string;
  period?: SpecPreset;
  from?: string;
  to?: string;
}

/** What the viewer has on the bar. */
export interface Bar {
  filters: FilterNode[];
  period: DateRangeValue;
}

/** The period and filters a saved spec opens on. */
export function savedViewOf(spec: ReportSpec): SavedView {
  return {
    // `?? []`: a spec stored before filters had a default carries none.
    filters: [...(spec.filters ?? [])],
    period: { preset: spec.period, from: spec.from, to: spec.to },
  };
}

/** A spec's period as the bar shows it. A spec with no macro is a custom range. */
export function periodOf(spec: Pick<ReportSpec, 'period' | 'from' | 'to'>): DateRangeValue {
  return { preset: spec.period ?? 'custom', from: spec.from, to: spec.to };
}

/** The bar a page opens on: the link's, field by field, else the saved one. */
export function readBar(search: BarSearch, saved: SavedView): Bar {
  const filters = readFilters(search.f, saved.filters);
  const period =
    search.period || search.from
      ? { preset: search.period ?? 'custom', from: search.from, to: search.to }
      : periodOf({ period: saved.period.preset, from: saved.period.from, to: saved.period.to });
  return { filters, period };
}

/**
 * `spec` with `bar` on it — the structure of one, the period and filters of
 * the other.
 *
 * A macro is stored with the window it means today, as the builder stores
 * one. A period the spec may not name keeps the spec's own.
 */
export function withBar(spec: ReportSpec, bar: Bar): ReportSpec {
  // The spec's OWN period, left exactly as stored — a fixed window saved with
  // no macro stays without one, rather than coming back as `custom`.
  if (samePeriod({ preset: spec.period, from: spec.from, to: spec.to }, bar.period)) {
    return { ...spec, filters: [...bar.filters] };
  }
  const named = (DATE_RANGE_PRESETS as readonly string[]).includes(bar.period.preset);
  const window = named ? resolveDateRange(bar.period) : null;
  return {
    ...spec,
    filters: [...bar.filters],
    ...(window && {
      period: bar.period.preset as SpecPreset,
      from: window.from,
      to: window.to,
    }),
  };
}

/** {@link withBar}, with a SAVED view: what an owner's Save stores. */
export function withSavedView(spec: ReportSpec, view: SavedView): ReportSpec {
  return withBar(spec, {
    filters: view.filters,
    period: periodOf({ period: view.period.preset, from: view.period.from, to: view.period.to }),
  });
}

/**
 * The URL for the bar on `spec`, against what is saved.
 *
 * Empty when the bar IS the saved view, so the plain link stays plain and
 * follows the saved view when an owner changes it. A macro is written by name
 * alone — its dates are today's answer to it, not part of the question.
 */
export function writeBar(spec: ReportSpec, saved: SavedView): BarSearch {
  const f = writeFilters(spec.filters, saved.filters);
  const current = periodOf(spec);
  const period: BarSearch = samePeriod(saved.period, current)
    ? {}
    : current.preset === 'custom'
      ? { period: 'custom', from: current.from, to: current.to }
      : { period: current.preset as SpecPreset };
  return { ...(f !== undefined && { f }), ...period };
}

/** One key per URL state, field order fixed, so equal views compare equal. */
export function barSearchKey(search: BarSearch): string {
  return JSON.stringify([search.f, search.period, search.from, search.to]);
}

/**
 * A saved report's draft reduced to what an owner's Save would change WITHOUT
 * the explicit action — its structure, name and description.
 *
 * The bar is left out (period, filters), and `offset` too: turning a page is
 * reading. Same shape as the workbench's `draftKey`, so its rename path can
 * patch the name and description into it.
 */
export function structureKey(draft: ReportDraft): string {
  return JSON.stringify({
    spec: draft.spec && {
      ...draft.spec,
      offset: undefined,
      filters: undefined,
      period: undefined,
      from: undefined,
      to: undefined,
    },
    name: draft.name,
    description: draft.description,
  });
}
